import React, { useEffect, useMemo, useState } from "react";
import { Typography, Divider, Button } from "@mui/material";
import { useDrawer } from "@/components/Drawer/context/DrawerContext";
import Table from "@/components/Table/Table";
import { Box } from "@mui/system";
import { formttedDate } from "@/lib/hyphen";
import ApprovedSalesDeliveryModal from "../component/ApprovedSalesDeliveryModal";
import { renderStatus } from "../component/OrderColumn";
import toast from "@/lib/toast";
import { formatAmount } from "@/lib/utils";

interface Props {
  orderId: any;
  fetchOrderApi: (id: any) => Promise<any>;
  isApproved? : boolean;
  onDeliver?: (row: any) => void;
  retailerName? : any;
  isreailerNameShown? : boolean;
}

const OrderDrawer: React.FC<Props> = ({ orderId, fetchOrderApi,isApproved,onDeliver,retailerName,isreailerNameShown = true }) => {
  const { closeDrawer } = useDrawer();

  const [orderData, setOrderData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [deliverModalOpen, setDeliverModalOpen] = useState(false);
  const [selectedOrderForDelivery, setSelectedOrderForDelivery] = useState<any>(null);
  
  const currency = localStorage.getItem("currency");

  const fetchOrderDetails = async () => {
    try {
      setLoading(true);
      
      const res = await fetchOrderApi(orderId);

      setOrderData(res?.data);
    } catch (error) {
      console.error("Order fetch failed", error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (!orderId) return;
    fetchOrderDetails();
  }, [orderId, fetchOrderApi]);

  const handleOpenDeliverModal = (row: any) => {

    setSelectedOrderForDelivery({
      ...row,
      id: orderId,
      retailerName: retailerName
    });
    setDeliverModalOpen(true);
  };
  const handleCloseDeliverModal = () => {
    setDeliverModalOpen(false);
    setSelectedOrderForDelivery(null);
  };

  const handleDeliverySuccess = () => {
    fetchOrderDetails();
    if (onDeliver) {
      onDeliver(selectedOrderForDelivery);
    }
    setDeliverModalOpen(false);
    setSelectedOrderForDelivery(null);
  };


  const columns = useMemo(
    () => [
      {
        name: "SKU Code",
        selector: (row: any) => row?.skuCode,
        sortable: true,
      },
      {
        name: "Product Name",
        selector: (row: any) => row?.productName,
        sortable: true,
      },
      {
        name: `Price (${currency})`,
        selector: (row: any) =><span>{formatAmount(row?.price)}</span>,
        sortable: true,
      },
      {
        name: "Quantity",
        selector: (row: any) => row?.quantity,
        sortable: true,
      },
      {
        name: "Status",
        selector: (row: any) => renderStatus(row?.orderDetailStatus),
      },
      ...(isApproved
      ? [
         {
        name: "Action",
        selector: (row: any) =>row?.orderDetailStatus === "Approved" ? (
          <Button
            variant="outlined"
            onClick={() => handleOpenDeliverModal(row)}
          >
            Deliver
          </Button>
        ) : null,
      }
      ] : [])
    ],
    [currency,isApproved]
  );

  

  return (
    <div style={{ padding: 20 }}>
      <Typography variant="h6" gutterBottom>
        Order Items
      </Typography>

      <Divider sx={{ mb: 2 }} />

      <Box display="grid" gridTemplateColumns="1fr 1fr 1fr" gap={2}>
        {isreailerNameShown && (
        <Typography>
          <strong>Retailer Name</strong>
          <p>{orderData?.retailerName || "-"}</p>
        </Typography>
        )}

        <Typography>
          <strong>Order Number</strong>
          <p>{orderData?.orderNumber || "-"}</p>
        </Typography>
      
        <Typography>
          <strong>Order Date</strong>
          <p>{formttedDate(orderData?.orderDate)}</p>
        </Typography>
        <Typography>
          <strong>Shipping Address</strong>
          <p>{orderData?.shippingAddress || "-"}</p>
        </Typography>
        <Typography>
          <strong>Total Price</strong>
          <p>{`${currency} ${formatAmount(orderData?.totalPrice || 0)}`}</p>
        </Typography>
        {orderData?.status === "Rejected" && (
        <Typography>
          <strong>Rejected Reason</strong>
          <p>{orderData?.rejectReason || "-"}</p>
        </Typography>
        )}
      </Box>
      
      <Divider sx={{ my: 2 }} />

      <Table columns={columns} data={orderData?.orderDetails ?? []} />

      <Divider sx={{ my: 2 }} />

      <Button variant="outlined" fullWidth onClick={closeDrawer}>
        Close
      </Button>
      <ApprovedSalesDeliveryModal
        isOpen={deliverModalOpen}
        onClose={handleCloseDeliverModal}
        orderData={selectedOrderForDelivery}
        onSuccess={handleDeliverySuccess}
        retailerName={retailerName}
      />
    </div>
  );
};

export default OrderDrawer;
